import { useEffect, useRef, useState } from 'react';

type ManifestLaunch = {
  version: '1.2' | '2004' | 'unknown';
  launchHref: string;
  resourceId?: string;
};

const NS = {
  adlcp: 'http://www.adlnet.org/xsd/adlcp_rootv1p2',
};

// ---- Minimal SCORM 1.2 API (enough to let content boot + avoid crashes) ----
type Scorm12API = {
  LMSInitialize: (p?: string) => string;
  LMSFinish: (p?: string) => string;
  LMSGetValue: (k: string) => string;
  LMSSetValue: (k: string, v: string) => string;
  LMSCommit: (p?: string) => string;
  LMSGetLastError: () => string;
  LMSGetErrorString: (code: string) => string;
  LMSGetDiagnostic: (code: string) => string;
};

function installScorm12API(
  getStore: () => Record<string, string>,
  setStore: (next: Record<string, string>) => void
) {
  // Avoid re-install if already exists
  if (typeof window !== 'undefined' && (window as any).API) return;

  let lastError = '0';
  let initialized = false;

  const api: Scorm12API = {
    LMSInitialize: () => {
      initialized = true;
      lastError = '0';
      return 'true';
    },
    LMSFinish: () => {
      initialized = false;
      lastError = '0';
      return 'true';
    },
    LMSGetValue: (k) => {
      if (!initialized) {
        lastError = '301'; // not initialized
        return '';
      }
      lastError = '0';
      const store = getStore();
      return store[k] ?? '';
    },
    LMSSetValue: (k, v) => {
      if (!initialized) {
        lastError = '301';
        return 'false';
      }
      lastError = '0';
      const store = getStore();
      setStore({ ...store, [k]: String(v ?? '') });
      return 'true';
    },
    LMSCommit: () => {
      if (!initialized) {
        lastError = '301';
        return 'false';
      }
      lastError = '0';
      // You can POST getStore() to your backend here if needed.
      return 'true';
    },
    LMSGetLastError: () => lastError,
    LMSGetErrorString: (code) => {
      // Minimal mapping
      const map: Record<string, string> = {
        '0': 'No error',
        '301': 'Not initialized',
        '101': 'General exception',
      };
      return map[String(code)] ?? 'Unknown error';
    },
    LMSGetDiagnostic: (code) => `SCORM12 diagnostic: ${code}`,
  };

  (window as any).API = api;
}

// ---- Manifest parsing (your logic kept, small hardening) ----
export function parseScormManifest(xmlText: string): ManifestLaunch {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror'))
    throw new Error('Invalid imsmanifest.xml');

  const schemaVersion =
    doc.getElementsByTagName('schemaversion')?.[0]?.textContent?.trim() ?? '';

  const version: ManifestLaunch['version'] =
    schemaVersion === '1.2'
      ? '1.2'
      : schemaVersion.includes('2004')
      ? '2004'
      : 'unknown';

  const orgs = doc.getElementsByTagName('organizations')[0];
  const defaultOrgId = orgs?.getAttribute('default') || '';

  let identifierRef = '';
  if (defaultOrgId) {
    const orgList = Array.from(doc.getElementsByTagName('organization'));
    const defaultOrg = orgList.find(
      (o) => o.getAttribute('identifier') === defaultOrgId
    );
    const item = defaultOrg?.getElementsByTagName('item')?.[0];
    identifierRef = item?.getAttribute('identifierref') || '';
  }

  const resources = Array.from(doc.getElementsByTagName('resource'));

  let chosenResource: Element | undefined;
  if (identifierRef) {
    chosenResource = resources.find(
      (r) => r.getAttribute('identifier') === identifierRef
    );
  }

  if (!chosenResource) {
    chosenResource =
      resources.find(
        (r) => r.getAttributeNS(NS.adlcp, 'scormtype') === 'sco'
      ) ||
      resources.find((r) => r.getAttribute('adlcp:scormtype') === 'sco') ||
      resources[0];
  }

  const launchHref = chosenResource?.getAttribute('href')?.trim() || '';
  if (!launchHref) throw new Error('Launch file not found in manifest');

  return {
    version,
    launchHref,
    resourceId: chosenResource?.getAttribute('identifier') || undefined,
  };
}

// ---- Helpers ----
function joinUrl(base: string, path: string) {
  const b = base.replace(/\/+$/, '');
  const p = path.replace(/^\/+/, '');
  return `${b}/${p}`;
}

/** Resolve to an absolute URL so new URL() never receives a relative path. */
function toAbsoluteUrl(urlOrPath: string, base?: string): string {
  const trimmed = (urlOrPath || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const baseUrl =
    base && /^https?:\/\//i.test(base)
      ? base
      : typeof window !== 'undefined'
      ? window.location.origin +
        (base?.startsWith('/') ? base : '/' + (base || ''))
      : '';
  try {
    return new URL(trimmed, baseUrl).toString();
  } catch {
    return '';
  }
}

function addQuery(url: string, params: Record<string, string>) {
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
    return u.toString();
  } catch {
    return url;
  }
}

function isSameOrigin(url: string) {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function ScormPlayer({ folderUrl }: { folderUrl: string }) {
  const [launchUrl, setLaunchUrl] = useState<string>('');
  const [err, setErr] = useState<string>('');
  const [warning, setWarning] = useState<string>('');

  // Store CMI in-memory (replace with Redux/DB if you want)
  const cmiRef = useRef<Record<string, string>>({});

  useEffect(() => {
    // Install SCORM 1.2 API in parent (helps many packages boot)
    installScorm12API(
      () => cmiRef.current,
      (next) => {
        cmiRef.current = next;
      }
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setErr('');
        setWarning('');
        setLaunchUrl('');

        const base = folderUrl.replace(/\/$/, '');
        const manifestUrl = joinUrl(base, 'imsmanifest.xml');

        const res = await fetch(manifestUrl, { credentials: 'include' });
        if (!res.ok) throw new Error(`Manifest fetch failed: ${res.status}`);

        const xml = await res.text();
        const { launchHref } = parseScormManifest(xml);

        const rawLaunchUrl = joinUrl(base, launchHref);
        const resolveBase =
          base.startsWith('http') && base.length > 10
            ? base
            : typeof window !== 'undefined'
            ? window.location.origin
            : '';
        const absoluteLaunchUrl = toAbsoluteUrl(rawLaunchUrl, resolveBase);
        if (!absoluteLaunchUrl) {
          throw new Error('Invalid SCORM launch URL');
        }

        // Try to encourage standalone mode + debug (some packages support it)
        const maybeStandalone = addQuery(absoluteLaunchUrl, {
          StandAlone: 'true',
          ShowDebug: 'false',
        });

        if (!cancelled) {
          setLaunchUrl(maybeStandalone);

          // Cross-origin warning (this is the real blocker for strict packages)
          if (!isSameOrigin(maybeStandalone)) {
            try {
              setWarning(
                `SCORM is loading from a different origin (${
                  new URL(maybeStandalone).origin
                }). ` +
                  `Some packages will fail unless you serve SCORM from the same origin (proxy) or host a shell on that origin.`
              );
            } catch {
              setWarning('SCORM is loading from a different origin.');
            }
          }
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || 'Failed to load SCORM');
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [folderUrl]);

  if (err) return <div style={{ padding: 12 }}>Error: {err}</div>;
  if (!launchUrl) return <div style={{ padding: 12 }}>Loading SCORM…</div>;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* {warning ? (
        <div
          style={{
            padding: 12,
            background: '#fff3cd',
            borderBottom: '1px solid #eee',
          }}
        >
          {warning}
        </div>
      ) : null} */}

      <iframe
        src={launchUrl}
        style={{ width: '100%', height: '100%', border: 0 }}
        allow="fullscreen"
        title="SCORM Player"
      />
    </div>
  );
}
